<template>
    <app-layout class="billing-container">
        <common-content-spacer height="medium">
            <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>
            <common-page-header :heading="pageTitle" size="medium"></common-page-header>
            <common-page-split spacing="medium">
                <template #aside>
                    <common-content-group :label="$t('pages.practice.billing.active_plan')">
                        <common-content-spacer height="medium">
                            <div>
                                <common-page-header
                                    :heading="planHeading"
                                    size="page-plain-header"
                                    contentSpacing="medium"
                                    :isCenter="true"
                                ></common-page-header>
                                <p class="billing-container--side-info">
                                    {{ $t('pages.practice.billing.billing_side_info') }}
                                </p>
                            </div>
                            <billing-features-list :featuresList="featuresList"></billing-features-list>
                            <p class="billing-container--side-features--info">
                                {{ $t('pages.practice.billing.appointment_fee') }}
                            </p>
                            <p
                                v-if="$auth.getPractice().subscription === PracticePlanEnum.TRIAL_PERIOD"
                                class="billing-container--side-features--info"
                            >
                                {{ $t('pages.practice.billing.free_trial_ends', { date: freeTrialPeriod }) }}
                            </p>
                            <common-buttons v-if="!$auth.isPracticePremium()">
                                <common-button
                                    :label="$t('pages.practice.billing.upgrade_now')"
                                    size="large"
                                    type="primary"
                                    @click="billingPricingModalOpen = true"
                                ></common-button>
                            </common-buttons>
                            <p
                                v-if="$auth.isPracticePremium()"
                                class="billing-container--side-features--cancel"
                                @click="openAreYouSureModal"
                            >
                                {{ $t('pages.practice.billing.cancel_subscription') }}
                            </p>
                        </common-content-spacer>
                    </common-content-group>
                </template>
                <template #main>
                    <billing-payment-method v-if="$auth.isPracticePremium()" :paymentInfo="paymentInfo"></billing-payment-method>
                    <billing-table :invoiceData="invoiceData"></billing-table>
                </template>
            </common-page-split>
        </common-content-spacer>
        <billing-pricing-modal v-if="billingPricingModalOpen" @close="billingPricingModalOpen = false"></billing-pricing-modal>
        <common-are-you-sure-modal
            v-if="isAreYouSureModalOpen"
            :headingText="$t('pages.referrals.show.disclaimer_modal.heading')"
            :contentText="$t('pages.practice.billing.cancel_subscription_info')"
            :noButtonText="$t('common.microcopy.false')"
            :yesButtonText="$t('common.microcopy.true')"
            :showWarningIcon="true"
            @close="isAreYouSureModalOpen = false"
            @continue="cancelSubscription"
        ></common-are-you-sure-modal>
        <common-notification ref="commonSuccessNotification" :content="notificationMessage"></common-notification>
    </app-layout>
</template>

<script>
    import AppLayout from 'components/layouts/AppLayout'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import BillingPaymentMethod from 'components/page-elements/practice/billing/BillingPaymentMethod'
    import BillingTable from 'components/page-elements/practice/billing/BillingTable'
    import BillingFeaturesList from 'components/page-elements/practice/billing/BillingFeaturesList'
    import PracticesAPI from 'api/PracticesAPI'
    import BillingPricingModal from 'components/page-elements/practice/billing/BillingPricingModal'
    import PracticePlanEnum from '@/enums/PracticePlanEnum'
    import dayjs from 'dayjs'

    export default {
        mixins: [PageWithDataMixin],
        components: {
            AppLayout,
            BillingTable,
            BillingPaymentMethod,
            BillingFeaturesList,
            BillingPricingModal,
        },

        data() {
            return {
                featuresList: [],
                invoiceData: [],
                paymentInfo: {},
                PracticePlanEnum: PracticePlanEnum,
                billingPricingModalOpen: false,
                isAreYouSureModalOpen: false,
                notificationMessage: '',
            }
        },
        computed: {
            planHeading() {
                return this.$auth.getPractice().subscription === PracticePlanEnum.NETWORK
                    ? this.$t('pages.billing.billing.network')
                    : this.$t('pages.billing.billing.premium')
            },
            pageTitle() {
                return this.$t('pages.practice.billing.titles.plan_billing', { practiceName: this.$auth.getPractice().name })
            },

            pageBreadcrumbs() {
                return [
                    {
                        label: this.$t('pages.practice.billing.breadcrumbs.practice'),
                        route: { name: 'practice.profile' },
                    },
                    {
                        label: this.$t('pages.practice.billing.breadcrumbs.practice_billing'),
                    },
                ]
            },

            freeTrialPeriod() {
                return dayjs(this.$auth.getPractice().trialPeriodEnds).format('MMM DD YYYY')
            },
        },

        methods: {
            getInvoicesData() {
                PracticesAPI.getInvoicesData().then((response) => {
                    this.invoiceData = response.invoices
                    this.paymentInfo = response.card
                })
            },

            cancelSubscription() {
                PracticesAPI.cancelSubscription().then(() => {
                    this.isAreYouSureModalOpen = false
                    this.notificationMessage = this.$t('pages.practice.billing.subscription_canceled')
                    this.$refs.commonSuccessNotification.showNotification()
                    this.$auth.forceUpdatePractice()
                })
            },

            openAreYouSureModal() {
                this.isAreYouSureModalOpen = true
            },

            toggleBillingPricingModal() {
                this.billingPricingModalOpen = !this.billingPricingModalOpen
            },
        },

        mounted() {
            this.getInvoicesData()
            this.featuresList = [
                this.$t('pages.practice.billing.billing_list.item_1'),
                this.$t('pages.practice.billing.billing_list.item_2'),
                this.$t('pages.practice.billing.billing_list.item_3'),
                this.$t('pages.practice.billing.billing_list.item_4'),
                this.$t('pages.practice.billing.billing_list.item_5'),
                this.$t('pages.practice.billing.billing_list.item_6'),
            ]
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/components/practice-billing/_practice-billing.scss';
</style>
